import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { getRecordedWorkshops } from '@/lib/db'
import type { UnitWorkshop } from '@/types'
import {
  PlayCircle,
  Video,
  Calendar,
  Loader2,
  Pencil,
  Search,
  ExternalLink
} from 'lucide-react'

function toEmbedUrl(url: string): string | null {
  const match = url.match(/drive\.google\.com\/file\/d\/([^/]+)/)
  if (match) return `https://drive.google.com/file/d/${match[1]}/preview`
  if (url.includes('/preview')) return url
  return null
}

export default function RecordedWorkshopsList() {
  const [workshops, setWorkshops] = useState<UnitWorkshop[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [previewVideo, setPreviewVideo] = useState<UnitWorkshop | null>(null)

  useEffect(() => {
    async function load() {
      setLoading(true)
      const { data } = await getRecordedWorkshops()
      if (data) setWorkshops(data)
      setLoading(false)
    }
    load()
  }, [])

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('es-CL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  const getWorkshopTitle = (w: UnitWorkshop) => {
    const unitName = (w as any).unit?.title_es || (w as any).unit?.title || ''
    return unitName ? `Taller grabado - ${unitName}` : 'Taller grabado'
  }

  const filtered = workshops.filter(w =>
    getWorkshopTitle(w).toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#0055A4]" />
      </div>
    )
  }

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <PlayCircle className="w-7 h-7 text-[#0055A4]" />
            Talleres grabados
          </h1>
          <p className="text-gray-500 mt-1">
            {workshops.length} grabación{workshops.length !== 1 ? 'es' : ''} disponible{workshops.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input
          type="text"
          placeholder="Buscar por título..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
        />
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border">
          <Video className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-600">
            {searchTerm ? 'Sin resultados' : 'Sin grabaciones'}
          </p>
          <p className="text-gray-500">
            {searchTerm
              ? 'Intenta con otro término'
              : 'Los profesores aún no han subido grabaciones'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b text-left text-sm text-gray-500">
                <th className="px-4 py-3">Taller</th>
                <th className="px-4 py-3 hidden sm:table-cell">Cohorte</th>
                <th className="px-4 py-3 hidden sm:table-cell">Fecha</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((w) => {
                const title = getWorkshopTitle(w)

                return (
                  <tr key={w.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#0055A4]/10 rounded-lg flex items-center justify-center">
                          <PlayCircle className="w-5 h-5 text-[#0055A4]" />
                        </div>
                        <span className="font-medium">{title}</span>
                      </div>
                    </td>

                    <td className="px-4 py-3 hidden sm:table-cell">
                      {(w as any).cohort?.name || (
                        <span className="text-gray-500">Sin cohorte</span>
                      )}
                    </td>

                    <td className="px-4 py-3 hidden sm:table-cell">
                      <div className="flex items-center gap-1 text-gray-500">
                        <Calendar className="w-3.5 h-3.5" />
                        {w.scheduled_date ? formatDate(w.scheduled_date) : '—'}
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setPreviewVideo(w)}
                          className="p-2 text-[#0055A4] hover:bg-[#0055A4]/10 rounded-lg"
                        >
                          <PlayCircle className="w-4 h-4" />
                        </button>

                        <Link
                          to={`/admin/recorded-workshops/${w.id}/edit`}
                          className="p-2 text-gray-500 hover:bg-gray-200 rounded-lg"
                        >
                          <Pencil className="w-4 h-4" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {previewVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          role="button"
          tabIndex={0}
          aria-label="Cerrar preview"
          onClick={() => setPreviewVideo(null)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setPreviewVideo(null) }}
        >
          <span
          className="relative w-full max-w-4xl"
          onClick={(e) => e.stopPropagation()}
        >
            <div className="bg-black rounded-xl overflow-hidden">

              {previewVideo.recording_url && toEmbedUrl(previewVideo.recording_url) ? (
                <iframe
                  src={toEmbedUrl(previewVideo.recording_url)!}
                  title={getWorkshopTitle(previewVideo)}
                  className="w-full aspect-video"
                  allowFullScreen
                />
              ) : (
                <div className="w-full aspect-video flex items-center justify-center bg-gray-50 text-gray-500">
                  Video no disponible
                </div>
              )}

              <div className="p-4 bg-gray-50 flex justify-between">
                <h3 className="font-semibold">
                  {getWorkshopTitle(previewVideo)}
                </h3>

                <div className="flex gap-3">
                  {previewVideo.recording_url && (
                    <a
                      href={previewVideo.recording_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-[#0055A4] hover:underline flex items-center gap-1"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Abrir
                    </a>
                  )}

                  <button onClick={() => setPreviewVideo(null)}>
                    Cerrar
                  </button>
                </div>
              </div>

            </div>
           </span> 
        </div>
      )}
    </div>
  )
}